import React from "react";
import Plot from 'react-plotly.js';
import {Paper, Typography, useTheme} from "@mui/material";

export const BasicLineChart = ({
    title, 
    valueField, 
    data, 
    previousPeriodData,
    showTitle = false,
    showPaper = false,
    width,
    height = 300,
    responsive = false
}) => {
    const theme = useTheme();
    const dims = theme.dimensions;
    const panelTheme = theme.palette.contentPanel;
    const indicatorTheme = theme.palette.indicator;

    const hasCurrentData = data && data.length > 0;
    const hasPriorData = previousPeriodData && previousPeriodData.length > 0;

    if (!hasCurrentData && !hasPriorData) {
        if (showPaper) {
            return (
                <Paper style={{...panelTheme, padding: 20}}>
                    <Typography>No data available</Typography>
                </Paper>
            );
        }
        return <div/>;
    }
    
    let xTitle, xTickAngle = 'auto';

    const buildTrace = (traceData, colors, traceName) => {
        const traceYears = [...new Set(traceData.map(i => i.year))];
        const name = traceName || traceYears.join('-');
        let xData;
        if (traceData[0].day) {
            xData = traceData.map(i => {
                const y = `${traceYears.length > 1 ? i.year + '/' : ''}`;
                const m = `${i.month}`.padStart(2, '0');
                const d = `${i.day}`.padStart(2, '0');
                return `${y}${m}/${d}`;
            });
            xTitle = 'Day';
            xTickAngle = -45;
        } else if (traceData[0].month) {
            xData = traceData.map(i => {
                const y = `${traceYears.length > 1 ? i.year + '/' : ''}`;
                const m = `${i.month}`.padStart(2, '0');
                return `${y}${m}`;
            });
            xTitle = 'Month';
        } else if (traceData[0].quarter) {
            xData = traceData.map(i => {
                const y = `${traceYears.length > 1 ? i.year + '/' : ''}`;
                const q = `Q${i.quarter}`;
                return `${y}${q}`;
            });
            xTitle = 'Quarter';
        } else if (traceData[0].year) {
            xData = traceData.map(i => i.year);
            xTitle = 'Year';
        }
        return {
            x: xData,
            y: traceData.map(i => i[valueField] || 0),
            type: 'scatter',
            mode: 'lines',
            line: {color: colors.line, width: 2},
            fillcolor: colors.fill,
            fill: 'tozeroy',
            name: name,
            connectgaps: false,
            hovertemplate: responsive ? 
                '<b>%{fullData.name}</b><br>Period: %{x}<br>Value: %{y:.2f}<br><extra></extra>' :
                undefined
        };
    };

    const currentName = responsive && data?.[0]?.year ? `${data[0].year}` : undefined;
    const previousName = responsive && previousPeriodData?.[0]?.year ?
        `${previousPeriodData[0].year}` : undefined;

    const plotData = [];

    if (hasCurrentData) {
        plotData.push(buildTrace(data, indicatorTheme.plot.green, currentName));
    }

    if (hasPriorData) {
        plotData.push(buildTrace(previousPeriodData, indicatorTheme.plot.blue, previousName));
    }

    const allValues = [
        ...(data || []).map(d => d[valueField] || 0),
        ...(previousPeriodData || []).map(d => d[valueField] || 0)
    ].filter(v => v !== null && v !== undefined);
    
    const maxValue = allValues.length > 0 ? Math.max(...allValues) : 100;
    const minValue = allValues.length > 0 ? Math.min(...allValues) : 0;
    const padding = (maxValue - minValue) * 0.1;
    const yRange = responsive ? [Math.max(0, minValue - padding), maxValue + padding] : undefined;

    const chartWidth = width || (responsive ? undefined : dims.smallPanelWidth - 10);
    const chartHeight = height;
    const marginTop = responsive ? 20 : 50;
    const marginBottom = responsive ? 60 : 70;
    const marginLeft = responsive ? 60 : 40;
    const marginRight = responsive ? 40 : 30;

    const legendConfig = {x: 0.7, y: 1.3};

    const plotElement = (
        <Plot 
            data={plotData}
            layout={{
                autosize: responsive,
                width: chartWidth,
                height: chartHeight,
                margin: {t: marginTop, b: marginBottom, l: marginLeft, r: marginRight},
                xaxis: {
                    title: {text: xTitle},
                    tickmode: 'linear',
                    tickangle: xTickAngle,
                    categoryorder: 'category ascending'
                },
                yaxis: {
                    title: {text: ''},
                    range: yRange
                },
                showlegend: true,
                legend: legendConfig
            }}
            config={{
                displayModeBar: false,
                displaylogo: false,
                responsive: responsive
            }}
            style={responsive ? {width: '100%', height: '100%'} : undefined}
            useResizeHandler={responsive}
        />
    );

    if (showPaper) {
        return (
            <Paper style={{...panelTheme, padding: 20, height: height + 70, width: '100%'}}>
                {showTitle && title && (
                    <Typography variant="h6" gutterBottom color="primary">
                        {title}
                    </Typography>
                )}
                <div style={{width: '100%', height: `${height}px`}}>
                    {plotElement}
                </div>
            </Paper>
        );
    }

    return (
        <div style={{...panelTheme, marginTop: 10, padding: 5, width: width || dims.smallPanelWidth}}>
            {plotElement}
        </div>
    );
};